import { Button } from "@/components/ui/button";
import { useEffect, useRef, useState } from "react";
import { Award, Percent, Heart, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useIsMobile } from "@/hooks/use-mobile";
import { trackFormTriggerClicked } from "@/lib/posthog";
import { motion, AnimatePresence } from "framer-motion";

// Liste des phrases complètes à afficher
const phrases = ["Les meilleurs événements d'entreprise", "Les meilleurs séminaires d'entreprise", "Les meilleures conférences", "Les meilleures activités team building", "Les meilleures conventions", "Les meilleurs lancements de produit"];
interface HeroSectionProps {
  onOpenForm: () => void;
}
const HeroSection = ({
  onOpenForm
}: HeroSectionProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [phraseIndex, setPhraseIndex] = useState(0);
  const isMobile = useIsMobile();

  // Effet pour changer de phrase toutes les 3 secondes
  useEffect(() => {
    const interval = setInterval(() => {
      setPhraseIndex((prev) => (prev + 1) % phrases.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    console.log("Hero section mounted with YouTube video");
  }, []);
  const handleOpenForm = () => {
    trackFormTriggerClicked("Commencer gratuitement", "hero", "button");
    onOpenForm();
  };
  return <section className="relative h-[50vh] md:h-[75vh] w-full overflow-hidden">
      {/* Arrière-plan vidéo */}
      <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-black/80 to-gray-900/80 z-10">
        {/* Vidéo YouTube */}
        <div className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none">
          <iframe ref={iframeRef} src="https://www.youtube.com/embed/PKTqmkyynfM?autoplay=1&mute=1&controls=0&loop=1&playlist=PKTqmkyynfM&showinfo=0&rel=0&modestbranding=1&playsinline=1&enablejsapi=1&widgetid=1&vq=hd1080" title="Vidéo d'arrière-plan du séminaire" className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[100vw] min-w-full min-h-[200%] h-auto aspect-video scale-[2.5] md:scale-[1.3] lg:scale-[1.2]" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen loading="eager" style={{
          objectFit: 'cover'
        }} />
        </div>
        
        {/* Dégradé de superposition pour une meilleure lisibilité du texte */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-gray-900/60 to-black/60 z-10"></div>
      </div>

      {/* Superposition de contenu */}
      <div className={`relative z-20 flex flex-col h-full text-white px-6 ${isMobile ? 'items-center justify-center text-center' : 'items-center justify-center text-center pt-32'}`}>
        <div className="max-w-7xl mx-auto w-full flex flex-col items-center">
            {/* H1 avec animation fade entre les phrases */}
            <div className="h-[5rem] md:h-[4rem] lg:h-[5rem] mb-4 md:mb-8 overflow-hidden flex items-center justify-center">
              <AnimatePresence mode="wait">
                <motion.h1 key={phraseIndex} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -20
            }} transition={{
              duration: 0.5,
              ease: "easeInOut"
            }} className="text-2xl md:text-4xl lg:text-5xl font-bold tracking-tight text-balance">
                  {phrases[phraseIndex]}
                </motion.h1>
              </AnimatePresence>
            </div>
            
            {/* Section de réassurance */}
            <div className="flex flex-col items-center justify-center gap-2 md:gap-4 mb-4 md:mb-6 animate-fade-in animation-delay-700">
              <div className="flex items-center gap-2">
                <Award className="h-5 w-5 text-yellow-400" />
                <h2 className="text-sm font-normal">10 000+ événements organisés</h2>
              </div>
              <div className="flex items-center gap-2">
                <Percent className="h-5 w-5 text-green-400" />
                <h2 className="text-sm font-normal flex items-center gap-1">
                  Économisez de 10% à 30%
                  <TooltipProvider>
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger asChild>
                        <Info className="h-3.5 w-3.5 text-white/70 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-xs bg-black border-black">
                        <p className="text-sm text-white text-justify">Avec Naboo, bénéficiez de 10 % à 30 % d'économies par rapport à une réservation directe. Profitez de nos avantages négociés.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <Heart className="h-5 w-5 text-red-400" />
                <h2 className="text-sm font-normal">96% de satisfaction client</h2>
              </div>
            </div>
            
            {/* Bouton CTA */}
            <Button onClick={handleOpenForm} className="bg-cta-green text-cta-green-foreground hover:bg-cta-green/90 hover:shadow-xl hover:shadow-cta-green/30 text-sm px-6 py-2 font-semibold rounded-md animate-fade-in animation-delay-500 shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:translate-y-[-1px] hover:scale-105 transition-all bg-white">
              Commencer gratuitement
            </Button>
          </div>
      </div>
    </section>;
};
export default HeroSection;